// studioRollupDraft.js

import {
  createDefaultRollupDraft,
  getRollupAggregateOptions,
  normalizeRollupDefinition,
} from 'rollups/rollupDefinitions';
import { generateKey } from 'utilities/Keys';

function normalizeString(value) {
  return String(value || '').trim();
}

function resolveRollupFieldDef(fields, fieldKey) {
  const cleanedFieldKey = normalizeString(fieldKey);
  if (!cleanedFieldKey) return null;

  return (Array.isArray(fields) ? fields : []).find((field) => (
    normalizeString(field?.name || field?.key) === cleanedFieldKey
  )) || null;
}

/**
 * Converts a Studio or base rollup record into the shared editor draft shape.
 */
export function studioRollupRecordToEditorDraft(record, { typeKey = '' } = {}) {
  return normalizeRollupDefinition(record, {
    typeKey: normalizeString(typeKey) || null,
  });
}

/**
 * Creates a blank rollup editor draft for a new Studio rollup tab.
 */
export function createStudioRollupEditorDraft({ fields = [], typeKey = '' } = {}) {
  return createDefaultRollupDraft({
    fields,
    typeKey: normalizeString(typeKey) || null,
  });
}

/**
 * Normalizes a rollup editor draft for My Apps or installed persistence.
 */
export function editorDraftToStudioRollupRecord(editorDraft, { typeKey = '' } = {}) {
  return normalizeRollupDefinition(editorDraft, {
    typeKey: normalizeString(typeKey) || null,
  });
}

/**
 * Validates a Studio rollup editor draft.
 */
export function validateStudioRollupEditorDraft(editorDraft, { fields = [], typeKey = '' } = {}) {
  const payload = editorDraftToStudioRollupRecord(editorDraft, { typeKey });

  if (!normalizeString(payload?.title)) {
    return {
      isValid: false,
      error: 'Title is required.',
    };
  }

  if (!normalizeString(payload?.fieldKey)) {
    return {
      isValid: false,
      error: 'Field is required.',
    };
  }

  const fieldDef = resolveRollupFieldDef(fields, payload.fieldKey);
  const aggregateOptions = getRollupAggregateOptions(fieldDef);
  const cleanedAggregate = normalizeString(payload?.aggregate).toLowerCase();

  if (!aggregateOptions.some((option) => option.value === cleanedAggregate)) {
    return {
      isValid: false,
      error: 'Select a valid aggregate for this field.',
    };
  }

  return {
    isValid: true,
    payload: {
      ...payload,
      key: normalizeString(payload?.key) || generateKey(),
    },
  };
}
